import type { CardId, Rank } from './cards.js';

export type Seat = 0 | 1 | 2 | 3;
export const SEATS: readonly Seat[] = [0, 1, 2, 3];

/** Bir hamlede atılabilecek kart sayısı — tekli, ikili, üçlü, dörtlü. */
export type Count = 1 | 2 | 3 | 4;

export type Move =
  | { type: 'play'; cards: CardId[] }
  | { type: 'pass' };

export interface TrickPlay {
  seat: Seat;
  cards: CardId[];
}

/**
 * Tur (trick) durumu.
 *
 * count === 0 → tur henüz açılmadı, sıradaki oyuncu LİDER: istediği combo'yu
 * açar ve turun kart sayısını sabitler. Lider pas geçemez.
 *
 * YUMUŞAK PAS: pas geçen oyuncu turdan elenmez, sıra kendisine döndüğünde
 * tekrar oynayabilir. Bu yüzden tur bitişi "kim elendi" ile değil, ardışık
 * pas sayacı (passStreak) ile belirlenir.
 */
export interface Trick {
  count: 0 | Count;
  rank: Rank | null;
  /** Turu şu an elinde tutan oyuncu — en son kart atan. */
  holder: Seat | null;
  passStreak: number;
  plays: TrickPlay[];
}

export type HistoryEntry =
  | { kind: 'play'; seat: Seat; cards: CardId[] }
  | { kind: 'pass'; seat: Seat }
  | { kind: 'cut'; seat: Seat }
  | { kind: 'trickEnd'; winner: Seat; nextLeader: Seat }
  | { kind: 'finish'; seat: Seat; place: number };

export interface Deal {
  hands: CardId[][];
  turn: Seat;
  trick: Trick;
  /** Kartını bitirenler, bitirme sırasıyla. 3 kişi bitince el sona erer. */
  finishOrder: Seat[];
  /** Rütbe başına oynanmış kart sayısı (index 0 = rütbe 3). UI ve bot için. */
  playedByRank: number[];
  history: HistoryEntry[];
}

export interface ExchangeTransfer {
  from: Seat;
  to: Seat;
  cards: CardId[];
}

export interface ExchangePending {
  from: Seat;
  to: Seat;
  count: number;
}

/**
 * El sonu kart değişimi.
 *
 * Zorunlu yön: 4. → 1.'ye en yüksek 2 kartı, 3. → 2.'ye en yüksek 1 kartı.
 * Serbest yön: 1. → 4.'ye 2 kart, 2. → 3.'e 1 kart (oyuncu kendi seçer;
 * zorunlu kartlar zaten eline geçmiş olduğu için 15 kart içinden seçer).
 *
 * SET BAŞLARINDA DEĞİŞİM YOK: her set temiz sayfa açar. Yani 4 ellik bir sette
 * 3 değişim olur (2., 3. ve 4. elin öncesinde).
 */
export interface Exchange {
  forced: ExchangeTransfer[];
  pending: ExchangePending[];
  chosen: ExchangeTransfer[];
}

export type Phase = 'exchange' | 'play' | 'matchOver';

export interface MatchConfig {
  /** Bir sette kaç el oynanır. */
  dealsPerSet: number;
  /** Maçta kaç set oynanır. Toplam el = dealsPerSet * sets. */
  sets: number;
  /** Sıra başına saniye — timeout'ta takipçi pas geçer, lider en düşük tekliyi oynar. */
  turnSeconds: number;
  /** 1. ↔ 4. arasında değişen kart sayısı. */
  exchangeFirstLast: number;
  /** 2. ↔ 3. arasında değişen kart sayısı. */
  exchangeSecondThird: number;
}

export const DEFAULT_CONFIG: MatchConfig = {
  dealsPerSet: 4,
  sets: 4,
  turnSeconds: 15,
  exchangeFirstLast: 2,
  exchangeSecondThird: 1,
};

/** Bitiş sırasına göre puan: 1. → 4, 2. → 3, 3. → 2, 4. → 1. */
export const PLACE_POINTS: readonly number[] = [4, 3, 2, 1];

export interface Match {
  config: MatchConfig;
  seed: string;
  phase: Phase;
  /** 0'dan başlar; dealsPerSet * sets olunca maç biter. */
  dealIndex: number;
  scores: number[];
  deal: Deal | null;
  exchange: Exchange | null;
  /** Bir önceki elin bitiş sırası — sonraki elin liderini ve değişimi belirler. */
  lastFinishOrder: Seat[] | null;
  /**
   * Tamamlanan HER elin bitiş sırası, eskiden yeniye.
   *
   * Kümülatif skor "kim önde"yi söylüyor ama "hangi elde ne oldu"yu söylemiyor.
   * Puan tablosunun kaynağı bu; puanlar sıradan türüyor (`PLACE_POINTS`),
   * ayrıca saklanmıyor.
   */
  finishOrders: Seat[][];
  /**
   * Biten elin tamamı (geçmişiyle birlikte). El bitince `deal` yeni ele geçtiği
   * için son hamlelerin kaydı burada saklanır — el sonu ekranı, tekrar oynatma
   * ve istemcinin animasyon kuyruğu bunu kullanır.
   */
  lastDeal: Deal | null;
}

export class IllegalMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalMoveError';
  }
}
